import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useUpdateBedarf } from '../../../api/bedarfe';
import { useConfirm } from '../../../context/ConfirmContext';
import {
  Bedarf,
  BedarfStatus,
  ONBOARDING_STEPS,
  offeneOnboardingSchritte,
} from '../../../types/bedarf';
import { gewonnenRueckfrage } from '../../../utils/gewonnenRueckfrage';
import {
  BedarfActionConfig,
  BedarfModalType,
  STATUS_ACTIONS,
  SECONDARY_ACTIONS,
  DANGER_ACTIONS,
  EMAIL_PARENT_ACTION,
  isTerminalStatus,
} from './statusActions.config';

export interface ModalState {
  isOpen: boolean;
  open: () => void;
  close: () => void;
}

export interface UseBedarfActionsOptions {
  bedarf: Bedarf;
  onActionComplete?: () => void;
  includePrimaryActions?: boolean;
  includeSecondaryActions?: boolean;
  includeDangerActions?: boolean;
}

export interface UseBedarfActionsReturn {
  // All actions grouped
  primaryActions: BedarfActionConfig[];
  secondaryActions: BedarfActionConfig[];
  dangerActions: BedarfActionConfig[];
  allActions: BedarfActionConfig[];

  // Loading state
  isLoading: boolean;
  loadingAction: string | null;

  // Action handler
  handleAction: (actionConfig: BedarfActionConfig) => Promise<void>;

  // Modal states
  modals: {
    kennenlernen: ModalState;
    priority: ModalState;
  };
}

export function useBedarfActions({
  bedarf,
  onActionComplete,
  includePrimaryActions = true,
  includeSecondaryActions = true,
  includeDangerActions = true,
}: UseBedarfActionsOptions): UseBedarfActionsReturn {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [loadingAction, setLoadingAction] = useState<string | null>(null);

  // Modal states
  const [isKennenlernenOpen, setIsKennenlernenOpen] = useState(false);
  const [isPriorityOpen, setIsPriorityOpen] = useState(false);

  // Mutations
  const updateMutation = useUpdateBedarf();

  // Build actions for current status
  const primaryActions = useMemo(() => {
    if (!includePrimaryActions) return [];
    return STATUS_ACTIONS[bedarf.status] || [];
  }, [bedarf.status, includePrimaryActions]);

  const secondaryActions = useMemo(() => {
    if (!includeSecondaryActions) return [];

    const actions: BedarfActionConfig[] = [];

    // Add navigation actions with :id placeholder replaced
    SECONDARY_ACTIONS.forEach(action => {
      actions.push({
        ...action,
        navigateTo: action.navigateTo?.replace(':id', bedarf.id),
      });
    });

    // Add email action if parent email exists
    if (bedarf.parentEmail) {
      actions.push(EMAIL_PARENT_ACTION);
    }

    return actions;
  }, [bedarf.id, bedarf.parentEmail, includeSecondaryActions]);

  const dangerActions = useMemo(() => {
    if (!includeDangerActions) return [];

    // Don't show danger actions for terminal statuses
    if (isTerminalStatus(bedarf.status)) {
      return [];
    }

    return [...DANGER_ACTIONS];
  }, [bedarf.status, includeDangerActions]);

  const allActions = useMemo(() => {
    return [...primaryActions, ...secondaryActions, ...dangerActions];
  }, [primaryActions, secondaryActions, dangerActions]);

  // Modal state objects
  const modals = useMemo(() => ({
    kennenlernen: {
      isOpen: isKennenlernenOpen,
      open: () => setIsKennenlernenOpen(true),
      close: () => setIsKennenlernenOpen(false),
    },
    priority: {
      isOpen: isPriorityOpen,
      open: () => setIsPriorityOpen(true),
      close: () => setIsPriorityOpen(false),
    },
  }), [isKennenlernenOpen, isPriorityOpen]);

  // Main action handler
  const handleAction = useCallback(async (actionConfig: BedarfActionConfig) => {
    // Ignore disabled ("Bald verfügbar") actions
    if (actionConfig.disabled) return;

    // Handle navigation action
    if (actionConfig.action === 'navigate' && actionConfig.navigateTo) {
      navigate(actionConfig.navigateTo);
      return;
    }

    // Handle mailto action
    if (actionConfig.action === 'mailto') {
      if (bedarf.parentEmail) {
        window.location.href = `mailto:${bedarf.parentEmail}`;
      } else {
        toast.warning('Keine Eltern-E-Mail-Adresse vorhanden');
      }
      return;
    }

    // Handle modal action
    if (actionConfig.action === 'modal' && actionConfig.modalType) {
      const modalType = actionConfig.modalType as BedarfModalType;
      if (modalType === 'kennenlernen') setIsKennenlernenOpen(true);
      else if (modalType === 'priority') setIsPriorityOpen(true);
      return;
    }

    // Handle status change action
    if (actionConfig.action === 'statusChange' && actionConfig.newStatus) {
      // Open onboarding steps no longer block GEWONNEN, they ask first.
      if (actionConfig.newStatus === BedarfStatus.GEWONNEN) {
        const rueckfrage = gewonnenRueckfrage(
          offeneOnboardingSchritte(bedarf),
          ONBOARDING_STEPS.length,
          'bedarf',
        );
        if (rueckfrage && !(await confirm(rueckfrage))) return;
      }

      setLoadingAction(actionConfig.key);

      try {
        await updateMutation.mutateAsync({
          id: bedarf.id,
          data: { status: actionConfig.newStatus },
        });
        toast.success('Status erfolgreich geaendert');
        onActionComplete?.();
      } catch (error) {
        console.error('Status change failed:', error);
        toast.error('Status konnte nicht geaendert werden');
      } finally {
        setLoadingAction(null);
      }
    }
  }, [
    navigate,
    confirm,
    bedarf,
    updateMutation,
    onActionComplete,
  ]);

  return {
    primaryActions,
    secondaryActions,
    dangerActions,
    allActions,
    isLoading: loadingAction !== null,
    loadingAction,
    handleAction,
    modals,
  };
}
